import { gql } from '@apollo/client';
import { type HubspotDemos, Notice } from '@mnd-frontend/graphql-schema';
import { useStateWithOverwrite } from '@mnd-frontend/hooks';
import IconCircleCheck from '@mnd-frontend/icons/components/CircleCheck';
import { Dialog, Input, Text } from '@mnd-frontend/ui/publish/components';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import { SPACING_1 } from '@mnd-frontend/ui/shared/tokens';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useCurrentPressroom } from '../../contexts/CurrentPressroomContext';
import {
  useRequestTrialDemoMutation,
  useSetNoticeMutation,
  useTrialDemoBookingNoticeQuery,
} from '../../generated/hooks';
import { type TrackInput, track } from '../../lib/tracker';

gql`
  query TrialDemoBookingNotice($pressroomId: ID!) {
    pressroom(id: $pressroomId) {
      id
      pendingNotices {
        id
        trialDemo
        planUpsell
      }
    }
  }

  mutation bookTrialDemoNotice($pressroomId: ID!) {
    noticeConfirm(input: { notice: trialDemo, pressroomId: $pressroomId }) {
      pendingNotices {
        id
        trialDemo
      }
    }
  }

  mutation RequestTrialDemo($pressroomId: ID!, $hubspotType: [HubspotDemos!], $phone: String) {
    requestDemo(
      input: { pressroomId: $pressroomId, scope: { hubspotDemos: $hubspotType }, phone: $phone }
    ) {
      error {
        message
        ... on RequestDemoValidationError {
          phone
        }
      }
    }
  }

  mutation SetNotice($pressroomId: ID!, $notice: Notice!) {
    noticeConfirm(input: { notice: $notice, pressroomId: $pressroomId }) {
      pendingNotices {
        id
        ...BookingModalNotices
      }
    }
  }

  fragment BookingModalNotices on PendingNotices {
    id
    planUpsell
    trialDemo
  }
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_1};
`;

interface ContactBookingModalProps {
  show: boolean;
  defaultValues: { email: string; phone: string };
  close: () => void;
  onSuccessClose: () => void;
  hubspotType: `${HubspotDemos}`;
  tracking: TrackInput;
  acceptText?: string;
  noticeKey: Notice;
}

const ContactBookingModal = ({
  show,
  defaultValues,
  close,
  onSuccessClose,
  hubspotType,
  tracking,
  acceptText,
  noticeKey,
}: ContactBookingModalProps) => {
  const [phone, setPhone] = useStateWithOverwrite(defaultValues.phone);
  const { t } = useTranslation();
  const { pressroomId } = useCurrentPressroom();
  const { showFailureToast, showSuccessToast } = useToasts();

  const { data, loading } = useTrialDemoBookingNoticeQuery({
    variables: { pressroomId },
  });

  const [setNotice] = useSetNoticeMutation();

  const [requestDemo, { data: requestDemoData, loading: requestDemoMutationLoading }] =
    useRequestTrialDemoMutation({
      onCompleted: data => {
        const responseError = data.requestDemo?.error;
        if (responseError) {
          if (responseError.message) {
            showFailureToast(t('common_general_error'));
            close();
          }
        } else {
          track(tracking);
          showSuccessToast(
            `${t('trial_demo_booked_thank_you')} ${t('trial_demo_booked_we_contact_you')}`,
          );
          setNotice({ variables: { pressroomId, notice: noticeKey } });
          close();
        }
      },
    });

  const submit = () => {
    requestDemo({
      variables: {
        pressroomId,
        phone: phone,
        hubspotType: hubspotType as HubspotDemos,
      },
    });
  };

  const validationError =
    requestDemoData?.requestDemo?.error?.__typename === 'RequestDemoValidationError'
      ? requestDemoData.requestDemo.error
      : undefined;

  const demoBooked =
    !data?.pressroom?.pendingNotices?.[
      noticeKey === Notice.PlanUpsell ? 'planUpsell' : 'trialDemo'
    ];

  return (
    <Dialog
      open={show}
      description=""
      headline={demoBooked ? '' : t('trial_demo_form_modal_header')}
      confirmAction={{
        text: acceptText ?? t('trial_book_demo'),
        onClick: submit,
        loading: requestDemoMutationLoading,
      }}
      onClose={onSuccessClose}
      loading={loading}
    >
      {demoBooked ? (
        <>
          <IconCircleCheck $size="lg" $color="colorIconsSuccess" />
          <h1>{t('trial_demo_booked_thank_you')}</h1>
          <p>{t('trial_demo_booked_we_contact_you')}</p>
        </>
      ) : (
        <FormWrapper>
          <Text as="p" variant="body">
            {t('trial_demo_booking_form_info')}
          </Text>
          <Input
            label={t('common_email')}
            id="email"
            type="email"
            disabled
            value={defaultValues.email}
          />
          <Input
            id="phone"
            type="tel"
            label={t('common_phone_number')}
            value={phone ?? ''}
            onChange={e => setPhone(e.target.value)}
            error={validationError?.phone}
          />
        </FormWrapper>
      )}
    </Dialog>
  );
};

export default ContactBookingModal;
